import React from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { V4_DIALOG_CONTENT, V4_DIALOG_MD } from '../../theme/v4Compat';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useTranslation } from 'react-i18next';
import {
  DeviceUploadDeviceFirmwarePayload,
  HealthSystemDomainDeviceResultDeviceFirmwareViewModel
} from '../../api/HealthSystemApi';
import { MarkdownEditor } from '../../elements';
import { ApiService } from '../../hooks';
import { DeviceFirmwareType } from '../../models/device';
import { extractApiErrorMessage, notifications } from '../../utils';

interface IModalProps {
  opened: boolean;
  /**
   * Set to edit the description of an already uploaded firmware. The binary and the type belong to
   * the release and cannot be replaced, so those inputs are shown filled in and disabled.
   */
  firmware?: HealthSystemDomainDeviceResultDeviceFirmwareViewModel | null;
  /** Upload mode: the payload to upload, or null when the dialog was cancelled. */
  onModalAction: (form: DeviceUploadDeviceFirmwarePayload | null) => void;
  /** Edit mode: true once the description was saved, false when the dialog was cancelled. */
  onEditFinished?: (saved: boolean) => void;
}

/**
 * The version is not typed in: the backend parses it out of the file name and rejects a name it
 * cannot read, so the same expression runs here to catch it before the upload.
 */
const FIRMWARE_VERSION_PATTERN = /[RDT]\d+\.\d+\.\d+(LF|UHF)?/;

const FIRMWARE_TYPES = [
  { type: DeviceFirmwareType.Application, label: 'Application', Icon: SystemUpdateAltIcon },
  { type: DeviceFirmwareType.Bootloader, label: 'Bootloader', Icon: MemoryIcon },
  { type: DeviceFirmwareType.Bluetooth, label: 'Bluetooth', Icon: BluetoothIcon },
  { type: DeviceFirmwareType.RFID, label: 'RFID', Icon: SettingsInputAntennaIcon }
];

/** Marks a section whose value must be provided, the way MUI marks a required label. */
const RequiredMark = () => (
  <span aria-hidden className="ml-0.5 text-red-600">
    *
  </span>
);

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DeviceFirmwareModal: React.FC<IModalProps> = ({ opened, firmware, onModalAction, onEditFinished }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const isEditing = firmware != null;

  const [info, setInfo] = React.useState('');
  const [releaseNotes, setReleaseNotes] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [firmwareType, setFirmwareType] = React.useState<DeviceFirmwareType>(DeviceFirmwareType.Application);
  const [isDragging, setIsDragging] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const resetFields = React.useCallback(() => {
    setInfo(firmware?.info ?? '');
    setReleaseNotes(firmware?.releaseNotes ?? '');
    setFirmwareType((firmware?.deviceFirmwareType as DeviceFirmwareType) ?? DeviceFirmwareType.Application);
    setSelectedFile(null);
    setIsDragging(false);
    setShowErrors(false);
    setIsBusy(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [firmware]);

  // The dialog stays mounted between openings, so each opening starts from the firmware it was
  // opened for - an empty form when uploading, the stored description when editing.
  React.useEffect(() => {
    if (opened) {
      resetFields();
    }
  }, [opened, resetFields]);

  const detectedVersion = selectedFile ? (FIRMWARE_VERSION_PATTERN.exec(selectedFile.name)?.[0] ?? null) : null;
  const fileError = !isEditing && selectedFile === null ? t('f-val-firmware-file') : null;
  const versionError = selectedFile !== null && detectedVersion === null ? t('f-device-up-version-invalid') : null;
  const infoError = info.trim().length === 0 ? t('f-device-up-info-required') : null;
  const canSubmit = fileError === null && versionError === null && infoError === null && !isBusy;

  const cancel = () => {
    resetFields();

    if (isEditing) {
      onEditFinished?.(false);
      return;
    }

    onModalAction(null);
  };

  const upload = () => {
    if (!canSubmit || selectedFile === null) {
      setShowErrors(true);
      return;
    }

    onModalAction({
      Info: info.trim(),
      ReleaseNotes: releaseNotes.trim() === '' ? undefined : releaseNotes,
      Firmware: selectedFile,
      Type: firmwareType
    } as DeviceUploadDeviceFirmwarePayload);

    resetFields();
  };

  const save = () => {
    if (!canSubmit || firmware == null) {
      setShowErrors(true);
      return;
    }

    setIsBusy(true);

    apiService.api.device
      .deviceUpdateDeviceFirmwareDescription(
        { id: firmware.id },
        {
          info: info.trim(),
          releaseNotes: releaseNotes.trim() === '' ? null : releaseNotes
        }
      )
      .then(() => {
        notifications.success(t('f-device-release-notes-saved'));
        onEditFinished?.(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const selectFile = (file: File | undefined) => {
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(false);
    selectFile(event.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    setSelectedFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderFileCard = (name: string, secondary: React.ReactNode, onRemove?: () => void) => (
    <div className="box-border flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <InsertDriveFileOutlinedIcon className="!text-gray-400" />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{name}</p>
        <p className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">{secondary}</p>
      </div>

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('f-com-remove')}
          className="cursor-pointer rounded p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
        >
          <DeleteOutlineIcon fontSize="small" />
        </button>
      )}
    </div>
  );

  const renderVersionChip = (version: string) => (
    <span className="rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-800">{version}</span>
  );

  const renderFileSection = () => {
    // Editing shows which release is being described, without offering to replace it.
    if (isEditing) {
      return renderFileCard(firmware.fileName ?? '-', firmware.version ? renderVersionChip(firmware.version) : null);
    }

    if (selectedFile) {
      return renderFileCard(
        selectedFile.name,
        <>
          <span>{formatFileSize(selectedFile.size)}</span>
          {detectedVersion && renderVersionChip(detectedVersion)}
        </>,
        clearFile
      );
    }

    return (
      <button
        type="button"
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`box-border flex w-full cursor-pointer flex-col items-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
      >
        <CloudUploadIcon className="!text-gray-400" />
        <p className="text-sm font-medium text-gray-700">{t('f-device-up-file-drop')}</p>
        <p className="text-xs text-gray-500">{t('f-device-up-file-hint')}</p>
      </button>
    );
  };

  return (
    <Dialog id="deviceFirmwareDialog" open={opened} onClose={cancel} maxWidth="md" fullWidth sx={V4_DIALOG_MD}>
      <DialogTitle id="deviceFirmwareDialogTitle" component="div" className="!border-b !border-gray-200 !px-6 !py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            {isEditing ? <EditOutlinedIcon fontSize="small" /> : <CloudUploadIcon fontSize="small" />}
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900">
              {isEditing ? t('f-device-edit-description') : t('f-device-up-title')}
            </h2>
            <p className="text-xs text-gray-500">
              {isEditing ? t('f-device-edit-locked-hint') : t('f-device-up-subtitle')}
            </p>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5" sx={V4_DIALOG_CONTENT}>
        <div className="flex flex-col gap-5">
          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              {t('f-device-firmware-type')}
              {!isEditing && <RequiredMark />}
            </h3>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {FIRMWARE_TYPES.map(({ type, label, Icon }) => {
                const isSelected = firmwareType === type;
                const selectedStyle = 'border-blue-500 bg-blue-50 font-medium text-blue-700';

                if (isEditing) {
                  return (
                    <div
                      key={type}
                      aria-current={isSelected}
                      className={`box-border flex flex-col items-center gap-1 rounded-lg border px-3 py-3 text-sm ${
                        isSelected ? selectedStyle : 'border-gray-200 bg-gray-50 text-gray-400'
                      }`}
                    >
                      <Icon fontSize="small" />
                      {label}
                    </div>
                  );
                }

                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFirmwareType(type)}
                    aria-pressed={isSelected}
                    className={`box-border flex cursor-pointer flex-col items-center gap-1 rounded-lg border px-3 py-3 text-sm transition-colors ${
                      isSelected
                        ? selectedStyle
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon fontSize="small" />
                    {label}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              {t('f-device-firmware')}
              {!isEditing && <RequiredMark />}
            </h3>

            {!isEditing && (
              <input
                ref={fileInputRef}
                id="file-select"
                type="file"
                accept=".bin"
                className="hidden"
                onChange={(event) => selectFile(event.target.files?.[0])}
              />
            )}

            {renderFileSection()}

            {showErrors && fileError && <p className="mt-1 text-xs text-red-600">{fileError}</p>}
            {versionError && <p className="mt-1 text-xs text-red-600">{versionError}</p>}
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
              {t('f-device-up-info')}
              <RequiredMark />
            </h3>

            <TextField
              id="info-input"
              variant="outlined"
              // The asterisk lives on the heading above, so this only carries the semantics.
              required
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={info}
              onChange={(event) => setInfo(event.target.value)}
              disabled={isBusy}
              error={showErrors && infoError !== null}
              helperText={showErrors && infoError ? infoError : t('f-device-up-info-hint')}
              onBlur={() => setShowErrors(true)}
            />
          </section>

          <MarkdownEditor
            id="release-notes-input"
            label={t('f-device-up-release-notes')}
            hint={t('f-device-up-release-notes-hint')}
            placeholder={t('f-device-up-release-notes-placeholder')}
            value={releaseNotes}
            onChange={setReleaseNotes}
          />
        </div>
      </DialogContent>

      <DialogActions className="!border-t !border-gray-200 !px-6 !py-3">
        <Button onClick={cancel} variant="outlined" startIcon={<CloseIcon />} color="primary" disabled={isBusy}>
          {t('f-com-cancel')}
        </Button>

        {isEditing ? (
          <Button
            onClick={save}
            id="saveFirmwareDescriptionBtn"
            variant="contained"
            color="primary"
            disabled={!canSubmit}
          >
            {isBusy ? <CircularProgress size={20} /> : t('f-com-save')}
          </Button>
        ) : (
          <Button
            onClick={upload}
            id="uploadFirmwareBtn"
            startIcon={<CloudUploadIcon />}
            variant="contained"
            color="primary"
            disabled={!canSubmit}
          >
            {t('f-device-up-btn')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeviceFirmwareModal;
